import clsx from "clsx";
// Hooks
import { RefObject, useMemo, useState } from "react";
import { useLocation } from "@/Common/hooks/Location/useLocation";
// MUI
import { GridColDef, GridGroupingColDefOverride } from "@mui/x-data-grid-pro";
import { GridApi, GridRenderCellParams } from "@mui/x-data-grid-premium";

// Helpers
import { HEADER_CLASS_NAME } from "@/Washouts/constants";
import { formatWithParens, isAutoGeneratedFooter } from "@Layouts/components/Tables/DataSmartTable/helpers";
import { getCellClassNameFn } from "@/Common/helpers/Tables";
import { getDateInUTC, getFormattedDate } from "@dealerwave/dw-layer-date-converter";

// Components
import { TypographyCell } from "@/Layouts/components/Tables/DataSmartTable/additional/TypographyCell";
import { TypographyCellProps } from "@/Layouts/components/Tables/DataSmartTable/types";
import { TypographyHeader } from "@/Layouts/components/Tables/DataSmartTable/additional/TypographyHeader";
import { ExpandCollapseAllButtonHeader } from "@Layouts/components/TreeGrid/ExpandCollapseAllButtonHeader";

// Types
import { IRepairOrder } from "@/DataPages/types/technicianFlaggedTime/model";
import { prepareGroupColDef } from "@/Layouts/components/TreeGrid/prepareGroupColDef";
import { DATE_FORMAT } from "@/Constants/date";
import { Box, Menu, Typography } from "@mui/material";

// Constants
const FOOTER_SINGLE_COL_WIDTH = 145;
const DEFAULT_COL_WIDTH = 70;

interface IGenerateRenderingFnConfig {
	customFooterValue: string;
	parentInParens: boolean;
	customFormatFn?: (params: GridRenderCellParams) => string | null;
}

const GenerateParentFinishDate = (param: GridRenderCellParams) => {
	const { timezone } = useLocation();
	const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
	const open = Boolean(anchorEl);

	const { repairOrderCreationDate, inStockDate, purchaseDate } = param.row;
	const hasAtLeastOneDate = repairOrderCreationDate || inStockDate || purchaseDate;

	const handleClick = (event: React.MouseEvent<HTMLElement>) => {
		if (hasAtLeastOneDate) {
			setAnchorEl(event.currentTarget);
		}
	};

	const handleClose = () => {
		setAnchorEl(null);
	};

	return (
		<>
			<Box className="flex flex-row items-center justify-end">
				<Typography
					className={clsx("!font-sans !text-[12px]", hasAtLeastOneDate && "!text-primary !font-bold underline cursor-pointer")}
					onClick={handleClick}
				>
					Ref.Date(s)
				</Typography>
				({[repairOrderCreationDate, inStockDate, purchaseDate].filter(Boolean).length})
			</Box>
			<Menu
				anchorEl={anchorEl}
				open={open}
				onClose={handleClose}
				anchorOrigin={{
					vertical: "bottom",
					horizontal: "center",
				}}
				transformOrigin={{
					vertical: "top",
					horizontal: "center",
				}}
				slotProps={{
					paper: {
						className: "!rounded-xl",
					},
				}}
			>
				<Box className="px-5 py-3">
					{repairOrderCreationDate && (
						<Typography className="!font-sans !text-gray-text !text-[12px]">
							RO Creation Date:{" "}
							<Typography component="span" className="!font-sans-bold !text-gray-text !text-[12px]">
								{getFormattedDate(repairOrderCreationDate, timezone, DATE_FORMAT.usDateDashTime12h)}
							</Typography>
						</Typography>
					)}
					{inStockDate && (
						<Typography className="!font-sans !text-gray-text !text-[12px]">
							In Stock Date:{" "}
							<Typography component="span" className="!font-sans-bold !text-gray-text !text-[12px]">
								{getFormattedDate(inStockDate, timezone, DATE_FORMAT.usDateDashTime12h)}
							</Typography>
						</Typography>
					)}
					{purchaseDate && (
						<Typography className="!font-sans !text-gray-text !text-[12px]">
							Purchase Date:{" "}
							<Typography component="span" className="!font-sans-bold !text-gray-text !text-[12px]">
								{getFormattedDate(purchaseDate, timezone, DATE_FORMAT.usDateDash)}
							</Typography>
						</Typography>
					)}
				</Box>
			</Menu>
		</>
	);
};

const generateRenderingFn = (
	otherTypographyCellProps?: Partial<Omit<TypographyCellProps, "params">>,
	config: Partial<IGenerateRenderingFnConfig> = {}
) => {
	return (param: GridRenderCellParams) => {
		let formattedValue = config.customFormatFn ? config.customFormatFn(param) : param.formattedValue;

		if (config.parentInParens && param.row?.isParent) {
			if (param.field === "finishDate") {
				formattedValue = GenerateParentFinishDate(param);
			} else {
				formattedValue = formatWithParens(param.value);
			}
		}

		if (config.customFooterValue && isAutoGeneratedFooter(param.id)) {
			formattedValue = config.customFooterValue;
		}

		return <TypographyCell params={{ ...param, formattedValue }} {...otherTypographyCellProps} />;
	};
};

const baseColumnDefinition: Partial<GridColDef> = {
	align: "right",
	headerClassName: HEADER_CLASS_NAME,
	renderHeader: (params) => <TypographyHeader params={params} right={params.colDef.align === "right"} />,
	cellClassName: getCellClassNameFn(),
	renderCell: generateRenderingFn({}, { parentInParens: true }),
	resizable: false,
	type: "string",
};

const numberRendererFn = generateRenderingFn(
	{},
	{
		customFormatFn: (param) => (typeof param.value === "number" ? param.value.toFixed(2) : param.value),
	}
);

const customNumberRendererFn = generateRenderingFn(
	{},
	{
		parentInParens: true,
		customFormatFn: (param) => (isAutoGeneratedFooter(param.id) ? "" : param.value),
	}
);

export const useTechnitianDataTableConfig = (
	apiRef: RefObject<GridApi>,
	uniqueSTValues: string[],
	isHideTechName = false,
	hasNextPage = false
) => {
	const groupingColDef = useMemo<GridGroupingColDefOverride>(
		() => ({
			...prepareGroupColDef(),
			renderHeader: () => (hasNextPage ? null : <ExpandCollapseAllButtonHeader />),
			getApplyQuickFilterFn: () => null,
		}),
		[hasNextPage]
	);

	const columns: GridColDef[] = useMemo<GridColDef[]>(
		() => [
			{
				...baseColumnDefinition,
				field: "_id",
				headerName: "ID",
				width: 10,
				filterable: false,
				getApplyQuickFilterFn: () => null,
			},
			{
				...baseColumnDefinition,
				field: "repairOrderNo",
				headerName: "RO#",
				width: isHideTechName ? FOOTER_SINGLE_COL_WIDTH : DEFAULT_COL_WIDTH,
				colSpan: (value) => {
					return !value && !isHideTechName ? 2 : 1;
				},
				cellClassName: getCellClassNameFn(
					[],
					{},
					{
						footer: clsx("flex !justify-start", {
							"!pl-[26px]": !isHideTechName,
						}),
					}
				),
				renderCell: generateRenderingFn({}, { customFooterValue: "Total Compensation" }),
			},
			{
				...baseColumnDefinition,
				resizable: true,
				align: "left",
				field: "employeeName",
				headerName: "Tech Name",
				width: 130,
				minWidth: 130,
				maxWidth: 325,
			},
			{
				...baseColumnDefinition,
				field: "finishDate",
				headerName: "Date",
				type: "date",
				width: 100,
				valueGetter: (value) => value && getDateInUTC(value),
				renderCell: (params) => {
					if (!isAutoGeneratedFooter(params.id) && params.row.isParent) {
						return generateCustomUniqueCount(apiRef, params);
					}
					return generateRenderingFn(
						{},
						{}
					)({ ...params, formattedValue: getFormattedDate(params.value, "", DATE_FORMAT.monthAbbrDatePadded) });
				},
			},
			{
				...baseColumnDefinition,
				align: "left",
				field: "department",
				headerName: "Dept.",
				width: 80,
			},
			{
				...baseColumnDefinition,
				field: "operationCode",
				headerName: "Operation Code",
				width: 95,
				renderCell: customNumberRendererFn,
			},
			{
				...baseColumnDefinition,
				field: "startTime",
				headerName: "Start Time",
				width: 75,
				type: "number",
				renderCell: generateRenderingFn({}, { parentInParens: false }),
			},
			{
				...baseColumnDefinition,
				field: "finishTime",
				headerName: "Finish Time",
				width: 75,
				type: "number",
				renderCell: generateRenderingFn({}, { parentInParens: false }),
			},
			{
				...baseColumnDefinition,
				align: "left",
				field: "reason",
				headerName: "Reason",
				width: 70,
				renderCell: customNumberRendererFn,
			},
			{
				...baseColumnDefinition,
				field: "actualTime",
				headerName: "Actual Time",
				width: 85,
				type: "number",
				renderCell: numberRendererFn,
			},
			{
				...baseColumnDefinition,
				field: "bookedTime",
				headerName: "Booked Time",
				width: 91,
				type: "number",
				renderCell: numberRendererFn,
			},
			{
				...baseColumnDefinition,
				align: "left",
				field: "jobStatus",
				headerName: "ST",
				width: 60,
				type: "singleSelect",
				valueOptions: uniqueSTValues,
				renderCell: customNumberRendererFn,
			},
			{
				...baseColumnDefinition,
				field: "rate",
				headerName: "Rate",
				width: 80,
				type: "number",
				cellClassName: (param) => {
					const fn = getCellClassNameFn([], {}, { base: param.row?.isParent ? "flex !justify-start" : undefined });
					return fn(param);
				},
				renderCell: (param) => {
					const fn = generateRenderingFn(
						{ hints: { isMoney: !param.row?.isParent }, render: !param.row?.isParent ? "absolute" : "default" },
						{
							parentInParens: true,
						}
					);
					return fn(param);
				},
			},
			{
				...baseColumnDefinition,
				field: "extended",
				headerName: "Extended",
				width: 110,
				type: "number",
				renderCell: generateRenderingFn({ hints: { isMoney: true }, render: "absolute" }),
			},
			{
				...baseColumnDefinition,
				field: "job",
				headerName: "Job",
				width: 65,
			},
			{
				...baseColumnDefinition,
				align: "left",
				field: "payrollStatus",
				headerName: "Payroll Status",
				width: 70,
				renderCell: customNumberRendererFn,
			},
			{
				...baseColumnDefinition,
				align: "left",
				field: "saleType",
				headerName: "Sale Type",
				width: 60,
				renderCell: customNumberRendererFn,
			},
		],
		[isHideTechName, apiRef, uniqueSTValues]
	);
	return { columns, groupingColDef };
};

function generateCustomUniqueCount(apiRef: RefObject<GridApi>, params: GridRenderCellParams) {
	const values = apiRef.current
		?.getRowGroupChildren({
			applyFiltering: true,
			groupId: params.id,
		})
		.map((child) => {
			const model = apiRef.current?.getRow(child) as IRepairOrder;
			if (!model || !(params.colDef.field in model)) {
				return "";
			}
			return model?.[params.colDef.field as keyof IRepairOrder];
		});
	return generateRenderingFn({}, { parentInParens: true })({ ...params, value: new Set(values).size });
}
